/**
 * Hooks optimizados con React Query para POSPage
 *
 * @description Centraliza el data fetching del POS con caching automático
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import POSService, { type IPOSProduct } from '@/services/pos/posService';

// Tipos de productos permitidos en POS
const POS_ALLOWED_PRODUCT_TYPES = ['single', 'Single', 'SINGLE', 'unit'] as const;

/**
 * Filtra productos para mostrar solo los permitidos en POS
 */
const filterPOSProducts = (products: IPOSProduct[]): IPOSProduct[] => {
  return products.filter(product =>
    POS_ALLOWED_PRODUCT_TYPES.includes(product.type as any)
  );
};

/**
 * Hook para obtener productos de un POS (admin/staff)
 *
 * @param posId ID del POS
 * @description Caché de 5 minutos - Los productos del POS cambian frecuentemente
 * Automáticamente filtra productos para mostrar solo tipos permitidos (single, unit)
 */
export function usePOSProducts(posId: number | null) {
  return useQuery<IPOSProduct[], Error>({
    queryKey: [...queryKeys.products.byPos(posId ?? 0), 'pos-admin'],
    queryFn: async () => {
      if (!posId) return [];

      const productsData = await POSService.getProducts(posId);

      // Filtrar productos usando la función utilitaria
      const singleProducts = filterPOSProducts(productsData);

      // Logs para debugging y monitoreo
      console.log(`[POS-${posId}] Total productos recibidos: ${productsData.length}`);
      console.log(`[POS-${posId}] Productos filtrados (single/unit): ${singleProducts.length}`);
      console.log(`[POS-${posId}] Tipos encontrados:`, [...new Set(productsData.map(p => p.type))]);

      if (singleProducts.length === 0 && productsData.length > 0) {
        console.warn(`[POS-${posId}] No hay productos de tipo 'single' disponibles`);
      }

      return singleProducts;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    enabled: Boolean(posId), // Solo ejecutar si hay posId
    // Usa retry: 1 del default global
  });
}
